import * as React from 'react';
import { useEffect, useState } from 'react';
import { intl, useSingleAndDoubleClick } from '../../lib';
import type { ValidIconName } from './Icon';
import type {
  ClassNameProp,
  CollapsibleComponentProps,
  VoidCallback
} from '..';
import type { ChildrenProp, StateSetter } from '../common-types';
import { isEmptyString } from '../../util';
import { ButtonIcon } from './internal/ButtonIcon';

/**
 * Props for a {@link Collapsible} component.
 */
export interface CollapsibleProps
  extends Required<ChildrenProp>,
    ClassNameProp,
    Omit<CollapsibleComponentProps, 'collapsible'> {
  /**
   * Text of the collapse/expand trigger. Can also be an JSX element.
   */
  triggerText: string | JSX.Element;
  /**
   * If rendering of all the content of the Collapsible should be postponed until before it's opened for the first time.
   */
  lazyRender?: boolean;
  /**
   * Name of the icon that's shown when the collapsible is expanded.
   */
  openIcon?: ValidIconName;
  /**
   * Name of the icon that's shown when the collapsible is collapsed.
   */
  collapsedIcon?: ValidIconName;
  /**
   * Arbitrary content to add on the trigger without triggering expand/collapse.
   */
  triggerSibling?: React.ReactNode;
  /**
   * Grouping information for the Collapsible.
   */
  grouped?: {
    /**
     * Index of the Collapsible in the group.
     */
    index: number;
    /**
     * The index of the Collapsible in the group that is currently expanded.
     */
    currentOpenIndex: number;
    /**
     * State setter function for the currentOpenIndex.
     */
    setOpenIndex: StateSetter<number>;
  };
  /**
   * Callback for when the Collapsible is expanded.
   */
  onExpand?: VoidCallback;
  /**
   * Callback for when the Collapsible is collapsed.
   */
  onCollapse?: VoidCallback;
  /**
   * Aria-label to set for the region.
   */
  'aria-label': string;
  /**
   * Aria role for collapsible button section
   */
  collapsibleRole?: string;
  /**
   * Aria role for collapsible content section
   */
  contentRole?: string;
}

/**
 * Component to wrap content in a collapsible element which can be opened and closed.
 * Setting both `grouped` and `collapsed` is not allowed.
 *
 * @param triggerText
 * @param openInitially
 * @param lazyRender
 * @param children
 * @param openIcon
 * @param collapsedIcon
 * @param triggerSibling
 * @param grouped
 * @param onExpand
 * @param onCollapse
 * @param className
 * @param ariaLabel
 * @param collapsed
 * @param setCollapsed
 * @param collapsibleRole
 * @param contentRole
 * @param props the {@link CollapsibleProps} properties
 * @return the created {@link JSX.Element}
 * @constructor
 */
export function Collapsible({
  triggerText,
  openInitially = false,
  lazyRender = true,
  children,
  openIcon = 'materialicon-keyboard_arrow_up',
  collapsedIcon = 'materialicon-keyboard_arrow_down',
  triggerSibling,
  grouped,
  onExpand,
  onCollapse,
  className = '',
  'aria-label': ariaLabel,
  collapsed,
  setCollapsed,
  collapsibleRole = 'region',
  contentRole,
  ...props
}: Readonly<CollapsibleProps>): JSX.Element {
  // if the collapsed state is controlled from the outside
  const controlled = collapsed !== undefined && !!setCollapsed;
  // make sure only one of grouped and collapsed is set
  if (grouped && controlled) {
    throw new Error(
      'Setting both grouped and a controlled collapsed on a Collapsible component is not allowed. Please choose one of the 2 methods to control the collapsed state.'
    );
  }

  // internal state, when not controlled
  const [isOpenInternal, setIsOpenInternal] = useState(
    (!grouped && !controlled && openInitially) ||
      (grouped && grouped.index === grouped.currentOpenIndex)
  );

  // if currently open
  const isOpen = (controlled ? !collapsed : isOpenInternal) ?? false;

  // handle lazy rendering
  const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);
  const renderChildren =
    lazyRender && !isOpen && !hasBeenOpened ? null : children;

  useEffect(() => {
    // change local state depending on the outside currentOpenIndex state
    if (grouped && grouped.currentOpenIndex !== grouped.index) {
      setIsOpenInternal(false);
    }
  }, [grouped]);

  // create the click handler for expanding/collapsing
  // dblClickHandler to stop propagation
  const [clickHandler, dblClickHandler] = useSingleAndDoubleClick(() => {
    // isOpen in this context can be seen as 'isOpenPrevious',
    // which is why all the checks here seem backwards
    setHasBeenOpened(true);

    // notify the group
    if (grouped && !isOpen) {
      // update outside state if grouped
      grouped.setOpenIndex(grouped.index);
    }

    // set new value to the opposite of the previous value
    if (controlled) {
      setCollapsed?.(!collapsed);
    } else {
      setIsOpenInternal(!isOpen);

      // call onExpand or onCollapse callback
      if (onExpand && !isOpen) {
        onExpand();
      } else if (onCollapse && isOpen) {
        onCollapse();
      }
    }
  });

  // the trigger element
  const triggerElement = (
    <button
      type="button"
      className="trigger"
      onClick={clickHandler}
      onDoubleClick={dblClickHandler}
      aria-expanded={isOpen}
    >
      <div className="trigger-text">{triggerText}</div>
      <ButtonIcon
        iconName={isOpen ? openIcon : collapsedIcon}
        aria-label={intl(
          isOpen ? 'view.grid.collapse.tooltip' : 'view.grid.expand.tooltip'
        )}
        className="trigger-btn"
      />
    </button>
  );

  // collect all css classes that should be set to the root element
  const elementClasses = ['ehs-collapsible'];
  if (isOpen) {
    elementClasses.push('expanded');
  }
  if (!isEmptyString(className)) {
    elementClasses.push(className);
  }

  return (
    <div
      {...props}
      className={elementClasses.join(' ')}
      role={collapsibleRole}
      aria-label={ariaLabel}
    >
      {triggerSibling ? (
        <div className="trigger-container">
          {triggerElement}
          {triggerSibling}
        </div>
      ) : (
        triggerElement
      )}
      <div
        className={
          isOpen ? 'collapsible-content' : 'collapsible-content hidden'
        }
        role={contentRole}
      >
        {renderChildren}
      </div>
    </div>
  );
}
